// /src/components/tools/memberProfileCard/PerkThemeStarfieldCanvas.jsx
// Starry sky underlay: variable-size points (≤3px), density + distribution sliders.
// Above 65% density, extra stars concentrate along a curved milky-way band.

import React, { useEffect, useRef } from 'react';

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function clampQty(value, fallback = 50) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/** Deterministic PRNG (mulberry32). */
function createRng(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** Quadratic curve BL → mid bulge → TR (milky-way spine in % space). */
function milkyPoint(t, bulge) {
  const u = 1 - t;
  const mx = 0.5 + bulge * 0.12;
  const my = 0.42 - bulge * 0.08;
  return {
    x: u * u * 0.04 + 2 * u * t * mx + t * t * 0.96,
    y: u * u * 0.88 + 2 * u * t * my + t * t * 0.12,
  };
}

function buildStars(density, distribution, seed) {
  const rng = createRng(seed);
  const d = clamp01(density / 100);
  const dist = clamp01(distribution / 100);

  // Base field grows linearly with density
  const fieldCount = Math.round(28 + d * 240);

  // Milky-way densification kicks in after 65%, with a curved ramp
  const milkyT = density <= 65 ? 0 : (density - 65) / 35;
  const milkyFactor = milkyT <= 0 ? 0 : milkyT ** 1.55;
  const milkyCount = Math.round(milkyFactor * (320 + dist * 280));

  // Band width: low distribution = wider haze, high = tight spine
  const bandSigma = 0.22 - dist * 0.16;

  const stars = [];

  for (let i = 0; i < fieldCount; i += 1) {
    const roll = rng();
    // Mostly tiny, few medium, rare near-max (≤3px)
    let size;
    if (roll < 0.62) size = 0.55 + rng() * 0.7;
    else if (roll < 0.9) size = 1.25 + rng() * 0.9;
    else size = 2.15 + rng() * 0.85;
    size = Math.min(3, size);

    stars.push({
      x: rng(),
      y: rng(),
      size,
      alpha: 0.28 + rng() * 0.62,
      cool: rng() < 0.18,
    });
  }

  for (let i = 0; i < milkyCount; i += 1) {
    const t = rng();
    const along = milkyPoint(t, dist);
    const angle = Math.atan2(-0.76, 0.92) + Math.PI / 2;
    const spread = (rng() + rng() + rng() - 1.5) * bandSigma;
    const x = along.x + Math.cos(angle) * spread;
    const y = along.y + Math.sin(angle) * spread;
    if (x < -0.02 || x > 1.02 || y < -0.02 || y > 1.02) continue;

    const roll = rng();
    let size;
    if (roll < 0.72) size = 0.5 + rng() * 0.65;
    else if (roll < 0.94) size = 1.1 + rng() * 0.85;
    else size = 2.0 + rng() * 1.0;
    size = Math.min(3, size);

    stars.push({
      x,
      y,
      size,
      alpha: 0.22 + rng() * 0.55 + milkyFactor * 0.12,
      cool: rng() < 0.28,
      milky: true,
    });
  }

  return stars;
}

/**
 * @param {{ density?: number, distribution?: number }} props
 */
export default function PerkThemeStarfieldCanvas({
  density = 50,
  distribution = 45,
}) {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const dens = clampQty(density, 50);
  const dist = clampQty(distribution, 45);

  useEffect(() => {
    starsRef.current = buildStars(dens, dist, 0x51a4f00d ^ (dens * 997) ^ (dist * 131));
  }, [dens, dist]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let disposed = false;

    const paint = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const w = parent.clientWidth || 1;
      const h = parent.clientHeight || 1;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const cw = Math.max(1, Math.round(w * dpr));
      const ch = Math.max(1, Math.round(h * dpr));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // Soft space plate so stars read without a media background
      ctx.fillStyle = 'rgba(6, 12, 22, 0.55)';
      ctx.fillRect(0, 0, w, h);

      const stars = starsRef.current;
      for (let i = 0; i < stars.length; i += 1) {
        const s = stars[i];
        const px = s.x * w;
        const py = s.y * h;
        const r = Math.min(3, s.size) / 2;
        ctx.beginPath();
        ctx.fillStyle = s.cool
          ? `rgba(170, 210, 255, ${s.alpha.toFixed(3)})`
          : `rgba(245, 248, 255, ${s.alpha.toFixed(3)})`;
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Soft milky haze when density is high — feathered blobs (no hard band edges)
      if (dens > 65) {
        const strength = ((dens - 65) / 35) ** 1.35;
        const bandHalf = (0.16 - (dist / 100) * 0.07) * Math.max(w, h);
        const cxCtrl = w * (0.5 + dist * 0.0012);
        const cyCtrl = h * 0.4;
        const samples = 28;

        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        for (let i = 0; i <= samples; i += 1) {
          const t = i / samples;
          const u = 1 - t;
          // Quadratic Bezier sample (same path as milky spine)
          const px = u * u * (w * 0.02) + 2 * u * t * cxCtrl + t * t * (w * 0.98);
          const py = u * u * (h * 0.9) + 2 * u * t * cyCtrl + t * t * (h * 0.1);
          const edgeFade = Math.sin(Math.PI * t); // fade at curve ends
          const radius = bandHalf * (0.75 + edgeFade * 0.55);
          const aCore = (0.028 + strength * 0.055) * edgeFade;
          const aMid = (0.014 + strength * 0.028) * edgeFade;
          const g = ctx.createRadialGradient(px, py, 0, px, py, radius);
          g.addColorStop(0, `rgba(200, 220, 245, ${aCore.toFixed(3)})`);
          g.addColorStop(0.35, `rgba(140, 175, 220, ${aMid.toFixed(3)})`);
          g.addColorStop(0.7, `rgba(90, 130, 180, ${(aMid * 0.35).toFixed(3)})`);
          g.addColorStop(1, 'rgba(90, 130, 180, 0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(px, py, radius, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    };

    const onResize = () => {
      if (disposed) return;
      paint();
    };

    paint();
    const ro = typeof ResizeObserver === 'function'
      ? new ResizeObserver(onResize)
      : null;
    if (ro && canvas.parentElement) ro.observe(canvas.parentElement);
    window.addEventListener('resize', onResize);

    return () => {
      disposed = true;
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, [dens, dist]);

  return (
    <div
      className="perk-theme-underlay__layer perk-theme-underlay__layer--starfield"
      aria-hidden
    >
      <canvas ref={canvasRef} className="perk-theme-underlay__starfield-canvas" />
    </div>
  );
}
